import { useAuth0 } from "@auth0/auth0-react";
import styles from "./journal.module.css";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Loader from "../../components/Loader";
import { Helmet } from "react-helmet";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import { ArrowLeft, Home, X } from "react-feather";
import Footer from "../../components/Footer";
import JournalFilter from "./components/JournalFilter";
import JournalCard from "./components/JournalCard";
import JournalMobileCard from "./components/JournalMobileCard";
import AddJournal from "./components/AddJournal";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { TableLoaderSmall } from "../../components/LoaderC";
import dayjs from "dayjs";
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";
import useDebouncedValue from "../../hooks/useDebouncedValue";

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const isActiveFilter = (value) => value !== "" && value !== "all" && value !== undefined && value !== null;

// Rows arrive newest date first from the server, so grouping keeps that order and a month
// that spans two loaded chunks stays under one header
const groupByMonth = (data) => {
    const groups = {};
    data.forEach(item => {
        const monthYear = dayjs(item.date).isValid() ? dayjs(item.date).format("MMMM YYYY") : "No Date";
        if (!groups[monthYear]) {
            groups[monthYear] = [];
        }
        groups[monthYear].push(item);
    });
    return groups;
};

const Journal = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [openAddForm, setOpenAddForm] = useState(false);
    const [openEditForm, setOpenEditForm] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [filterDateRange, setFilterDateRange] = useState(null);
    const [filterData, setFilterData] = useState({ searchKey: "", property: "", user: "" });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(0);
    const [journalDetails, setJournalDetails] = useState(null);
    const [propertyList, setPropertyList] = useState([]);
    const [tenantList, setTenantList] = useState([]);
    const [userList, setUserList] = useState([]);

    const [journals, setJournals] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const sentinelRef = useRef(null);
    const mobileSentinelRef = useRef(null);
    const requestSeq = useRef(0);
    const hasLoadedOnceRef = useRef(false);

    useEffect(() => {
        const getUserList = async () => {
            try {
                const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

                const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/journal-user-list", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                setUserList(response.data?.data || []);
            } catch (error) {
                console.error("[Journal] Failed to load user list", error);
                toast.error(error?.response?.data?.message || "Unable to load users. Please try again.");
            }
        }

        getUserList();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        const getPropertyList = async () => {
            try {
                const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

                const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/journal-property-list", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                setPropertyList(response.data?.data || []);
            } catch (error) {
                console.error("[Journal] Failed to load property list", error);
                toast.error(error?.response?.data?.message || "Unable to load properties. Please try again.");
            }
        }

        getPropertyList();
    }, [getAccessTokenSilently]);

    // Tenant options for the add/edit form and tenant names on the mobile cards
    useEffect(() => {
        async function fetchTenants() {
            try {
                const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

                const response = await axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/journal-tenant-list", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                let tenantOptions = [];
                (response.data?.data || []).forEach((item) => {
                    item?.options?.forEach((subItem) => {
                        tenantOptions.push({
                            group_id: item?.id,
                            group: item?.label,
                            value: subItem?.value,
                            label: subItem.label
                        });
                    });
                });

                setTenantList(tenantOptions);
            } catch (error) {
                console.error("[Journal] Failed to load tenant list", error);
                toast.error(error?.response?.data?.message || "Unable to load tenants. Please try again.");
            }
        }

        fetchTenants();
    }, [getAccessTokenSilently]);

    // Search is sent after typing pauses; property, user and date range apply immediately
    const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);
    const hasDateRange = Boolean(filterDateRange?.[0] && filterDateRange?.[1]);
    const startDate = hasDateRange ? dayjs(filterDateRange[0]).format("YYYY-MM-DD") : "";
    const endDate = hasDateRange ? dayjs(filterDateRange[1]).format("YYYY-MM-DD") : "";
    const query = useMemo(() => ({
        search: debouncedSearch.trim(),
        property: filterData.property,
        user: filterData.user,
        start: startDate,
        end: endDate,
    }), [debouncedSearch, filterData.property, filterData.user, startDate, endDate]);

    const fetchJournals = useCallback(async (currentOffset, replace) => {
        const seq = ++requestSeq.current;

        let activeIndicator = "loadingMore";
        if (replace && !hasLoadedOnceRef.current) {
            activeIndicator = "contentLoading";
            setContentLoading(true);
        } else if (replace) {
            activeIndicator = "refetching";
            setRefetching(true);
        } else {
            setLoadingMore(true);
        }
        setLoadMoreError(false);

        let params = { offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
        if (query.search !== "") params.search = query.search;
        if (isActiveFilter(query.property)) params.property_id = query.property;
        if (isActiveFilter(query.user)) params.user_id = query.user;
        if (query.start && query.end) {
            params.start = query.start;
            params.end = query.end;
        }

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/journals/list", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: params,
            });

            if (seq !== requestSeq.current) return;

            const newChunk = response.data?.data || [];
            const meta = response.data?.meta || {};

            setJournals((prev) => (replace ? newChunk : prev.concat(newChunk)));
            setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
            setHasMore(Boolean(meta?.has_more));

            setLoadError(false);
            hasLoadedOnceRef.current = true;
        } catch (error) {
            if (seq !== requestSeq.current) return;
            console.error("[Journal] Failed to load journal list", error);
            toast.error(error?.response?.data?.message || "Unable to load journals. Please try again.");

            if (replace) {
                setLoadError(true);
                setJournals([]);
                setHasMore(false);
            } else {
                setLoadMoreError(true);
            }
        } finally {
            if (seq === requestSeq.current) {
                if (activeIndicator === "contentLoading") setContentLoading(false);
                else if (activeIndicator === "refetching") setRefetching(false);
                else setLoadingMore(false);
            }
        }
    }, [getAccessTokenSilently, query]);

    // Load the first chunk again when search, filters, date range or data change
    useEffect(() => {
        fetchJournals(0, true);
    }, [fetchJournals, refreshKey]);

    // Infinite scroll: load the next chunk when a sentinel enters the viewport (desktop or mobile list)
    useEffect(() => {
        const desktopNode = sentinelRef.current;
        const mobileNode = mobileSentinelRef.current;
        if (!desktopNode && !mobileNode) return;

        const observer = new IntersectionObserver((entries) => {
            const isAnyIntersecting = entries.some((entry) => entry.isIntersecting);
            if (isAnyIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
                fetchJournals(offset, false);
            }
        }, { threshold: 0.1 });

        if (desktopNode) observer.observe(desktopNode);
        if (mobileNode) observer.observe(mobileNode);

        return () => observer.disconnect();
    }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchJournals]);

    const deleteConfirm = async () => {
        setLoading(true);
        setDeleteModalOpen(false);

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios.delete(process.env.REACT_APP_TENANT_API_URL + 'v1/journals/' + deleteId, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            setSnackbarMsg(response.data?.message);
            setSnackbarOpen(true);
            setDeleteId(0);
            setRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("[Journal] Failed to delete journal", error);
            toast.error(error?.response?.data?.message || "Unable to delete the journal. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    // The list is paginated on the server, so reload it after any change
    const addJournal = () => {
        setRefreshKey(prev => prev + 1);
    }

    const updateJournal = () => {
        setRefreshKey(prev => prev + 1);
    }

    const groupedJournals = groupByMonth(journals);

    return (<>
        {loading && <Loader />}

        <Helmet title="Journals" />

        <div className={`${styles.pageTitleWrap}`}>
            <Typography display="inline" className={`${styles.pageTitle}`}>Journal</Typography>
            <nav aria-label="breadcrumb">
                <ol className={`${styles.breadcrumb}`}>
                    <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Journal</li>
                    <li className={`${styles.breadcrumbItem} ${styles.active}`}>List</li>
                </ol>
            </nav>
        </div>

        <div className={`${styles.BodyCon}`}>
            <div className={`${styles.mainCardDiv}`}>
                <div className={`${styles.mainCard}`}>



                    <div className="control-pane">
                        <div className={`${styles.Row}`}>

                            <JournalFilter filterDateRange={filterDateRange} filterData={filterData} propertyList={propertyList} userList={userList} setFilterData={setFilterData.bind(this)} setOpenAddForm={setOpenAddForm.bind(this)} setFilterDateRange={setFilterDateRange.bind(this)} />

                            <div className={refetching ? `${styles.IncomeListingBody} ${styles.Refetching}` : `${styles.IncomeListingBody}`}>
                                {!contentLoading && Object.keys(groupedJournals).map(monthYear => (
                                    <React.Fragment key={monthYear}>
                                        <div className={styles.monthHeader}>
                                            <span>{monthYear}</span>
                                        </div>
                                        {groupedJournals[monthYear].map(item => (
                                            <JournalCard
                                                key={item.id}
                                                item={item}
                                                tenantList={tenantList}
                                                setDeleteId={setDeleteId.bind(this)}
                                                setDeleteModalOpen={setDeleteModalOpen.bind(this)}
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                                {!contentLoading && <div ref={sentinelRef} className={`${styles.ScrollSentinel}`} />}
                                {contentLoading && (<div className="Loader TableLoader">
                                    <TableLoaderSmall />
                                </div>)}
                            </div>

                            <div className={refetching ? `${styles.ListMobileView} ${styles.Refetching}` : `${styles.ListMobileView}`}>
                                {!contentLoading && Object.keys(groupedJournals).map(monthYear => (
                                    <React.Fragment key={monthYear}>
                                        <div className={styles.monthHeader}>
                                            <span>{monthYear}</span>
                                        </div>
                                        {groupedJournals[monthYear].map(item => (
                                            <JournalMobileCard
                                                key={item.id}
                                                item={item}
                                                tenantList={tenantList}
                                                setDeleteId={setDeleteId.bind(this)}
                                                setDeleteModalOpen={setDeleteModalOpen.bind(this)}
                                                setOpenEditForm={setOpenEditForm.bind(this)}
                                                setJournalDetails={setJournalDetails.bind(this)}
                                                setLoading={setLoading.bind(this)}
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                                {!contentLoading && <div ref={mobileSentinelRef} className={`${styles.ScrollSentinel}`} />}

                                {contentLoading && (<div className="Loader TableLoader">
                                    <TableLoaderSmall />
                                </div>)}
                            </div>

                            {loadingMore && (<div className={`${styles.ScrollStatus}`}>
                                <p>Loading more journals...</p>
                            </div>)}

                            {loadMoreError && (<div className={`${styles.ScrollStatus}`}>
                                <p>Couldn't load more journals.</p>
                                <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchJournals(offset, false)}>Retry</button>
                            </div>)}

                            {!contentLoading && loadError && (<div className={`${styles.NoDataMain}`}>
                                <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                                <p className={`${styles.NoDataTextSub}`}>We couldn't load the journals.</p>
                                <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchJournals(0, true)}>Retry</button>
                            </div>)}

                            {!contentLoading && !loadError && journals.length === 0 && (<div className={`${styles.NoDataMain}`}>
                                <div className={`${styles.NoDataIMG}`}>
                                    <img src="/static/img/no-journal.png" alt="No Data" />
                                </div>
                                <p className={`${styles.NoDataText}`}>All clear!</p>
                                <p className={`${styles.NoDataTextSub}`}>No unusual activities have been reported this week.</p>
                            </div>)}
                        </div>

                    </div>
                </div>
            </div>

            <Dialog
                open={openAddForm}
                onClose={(e) => setOpenAddForm(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="NewPopUp MobileForm"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle id="alert-dialog-title">
                    <button className={`${styles.BackAddBU}`} onClick={(e) => setOpenAddForm(false)}><ArrowLeft /></button>
                    Add New Journal
                    <button onClick={(e) => setOpenAddForm(false)}><X /></button>
                </DialogTitle>
                <DialogContent className={`${styles.RequestBodyPop}`}>
                    <AddJournal journalData={null} propertyList={propertyList} allTenantList={tenantList} setOpenAddForm={setOpenAddForm.bind(this)} setLoading={setLoading.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} onSubmitLocal={addJournal.bind(this)} />
                </DialogContent>
            </Dialog>

            <Dialog
                open={openEditForm}
                onClose={(e) => setOpenEditForm(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="NewPopUp MobileForm"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle id="alert-dialog-title">
                    <button className={`${styles.BackAddBU}`} onClick={(e) => setOpenEditForm(false)}><ArrowLeft /></button>
                    Edit Journal
                    <button onClick={(e) => setOpenEditForm(false)}><X /></button>
                </DialogTitle>
                <DialogContent className={`${styles.RequestBodyPop}`}>
                    <AddJournal journalData={journalDetails} propertyList={propertyList} allTenantList={tenantList} setOpenAddForm={setOpenEditForm.bind(this)} setLoading={setLoading.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} onSubmitLocal={updateJournal.bind(this)} />
                </DialogContent>
            </Dialog>

            <Dialog
                open={deleteModalOpen}
                onClose={(e) => setDeleteModalOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="NewPopUp"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle id="alert-dialog-title">Are you sure you want to delete the journal?<button onClick={(e) => setDeleteModalOpen(false)}><X /></button></DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <div className="ModalFormGroup">
                            <label className="PopupBodyText">The journal will be removed immediately. You can't undo this action.</label>
                        </div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <button className="CancelPopupBU" onClick={(e) => deleteConfirm()}>Delete</button>
                    <button onClick={(e) => setDeleteModalOpen(false)} className="SubmitPopupBU">Cancel</button>
                </DialogActions>
            </Dialog>


        </div>

        <Footer />

        <Snackbar
            open={snackbarOpen}
            onClose={(e) => setSnackbarOpen(false)}
            TransitionComponent={Slide}
            message={snackbarMsg}
            autoHideDuration={10000}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            action={
                <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={(e) => setSnackbarOpen(false)}
                >
                    <X />
                </IconButton>
            }
        />
    </>);
};

export default withCustomAuthenticationRequired(Journal, {
    onRedirecting: () => <Loader />,
});